<template>
  <main class="install-shell">
    <section class="install-intro">
      <div class="install-brand">
        <span class="install-brand__mark">☁</span>
        <span>云舟资源库</span>
      </div>
      <div>
        <span class="install-kicker">首次安装</span>
        <h1>几分钟完成系统配置</h1>
		<p>填写管理员与 MySQL 信息后，系统会校验当前域名或 IP 并完成初始化。</p>
      </div>
      <ul class="install-points">
		<li><span>1</span>向授权中心验证当前域名</li>
		<li><span>2</span>连接并准备 MySQL 数据库</li>
		<li><span>3</span>初始化管理员与系统数据</li>
      </ul>
    </section>

    <section class="install-card">
      <header>
        <h2>初始化配置</h2>
        <p>配置信息只保存在服务器的持久化数据卷中。</p>
      </header>

      <a-alert type="info" :show-icon="true" class="install-tip">
        MySQL 位于 Docker 宿主机时，数据库地址请填写 <code>host.docker.internal</code>，不要填写 127.0.0.1。
      </a-alert>

      <a-form :model="form" layout="vertical" @submit-success="install">
        <div class="install-section-title">管理员设置</div>
        <a-row :gutter="16">
          <a-col :xs="24" :md="12">
            <a-form-item field="adminUsername" label="后台账号" :rules="[{ required: true, minLength: 3, message: '至少输入 3 个字符' }]">
              <a-input v-model="form.adminUsername" placeholder="例如：admin" allow-clear />
            </a-form-item>
          </a-col>
          <a-col :xs="24" :md="12">
            <a-form-item field="adminPassword" label="后台密码" :rules="[{ required: true, minLength: 8, message: '至少输入 8 个字符' }]">
              <a-input-password v-model="form.adminPassword" placeholder="至少 8 个字符" allow-clear />
            </a-form-item>
          </a-col>
          <a-col :xs="24" :md="12">
            <a-form-item field="confirmPassword" label="确认密码" :rules="[{ required: true, message: '请再次输入密码' }]">
              <a-input-password v-model="form.confirmPassword" placeholder="再次输入后台密码" allow-clear />
            </a-form-item>
          </a-col>
		</a-row>

		<a-alert type="success" :show-icon="true" class="install-tip">
		  系统将使用程序标识 <code>yunzhou_wangpan</code> 校验当前域名或 IP，无需填写授权码。请先在 LicenseHub 后台添加授权。
		</a-alert>

		<div class="install-section-title">MySQL 数据库</div>
        <a-row :gutter="16">
          <a-col :xs="24" :md="16">
            <a-form-item field="databaseHost" label="数据库地址" :rules="[{ required: true, message: '请填写数据库地址' }]">
              <a-input v-model="form.databaseHost" placeholder="host.docker.internal 或远程 IP/域名" allow-clear />
            </a-form-item>
          </a-col>
          <a-col :xs="24" :md="8">
            <a-form-item field="databasePort" label="数据库端口" :rules="[{ required: true, message: '请填写端口' }]">
              <a-input-number v-model="form.databasePort" :min="1" :max="65535" hide-button />
            </a-form-item>
          </a-col>
          <a-col :xs="24" :md="12">
            <a-form-item field="databaseName" label="数据库库名" :rules="[{ required: true, match: /^[A-Za-z0-9_]+$/, message: '只能使用字母、数字和下划线' }]">
              <a-input v-model="form.databaseName" placeholder="quark_vault" allow-clear />
            </a-form-item>
          </a-col>
          <a-col :xs="24" :md="12">
            <a-form-item field="databaseUser" label="数据库账号" :rules="[{ required: true, message: '请填写数据库账号' }]">
              <a-input v-model="form.databaseUser" placeholder="quark_vault" allow-clear />
            </a-form-item>
          </a-col>
          <a-col :xs="24" :md="12">
            <a-form-item field="databasePassword" label="数据库密码">
              <a-input-password v-model="form.databasePassword" placeholder="数据库账号密码" allow-clear />
            </a-form-item>
          </a-col>
          <a-col :xs="24" :md="12">
            <a-form-item field="databaseSsl" label="连接安全">
              <div class="ssl-setting">
                <a-switch v-model="form.databaseSsl" />
                <span>启用 MySQL SSL</span>
              </div>
            </a-form-item>
          </a-col>
        </a-row>

        <div v-if="completed" class="install-complete">
          <a-spin />
          <div><strong>初始化完成</strong><p>容器正在自动重启，即将进入登录页面……</p></div>
        </div>

        <footer class="install-actions">
          <a-button :loading="testing" :disabled="installing || completed" @click="testDatabase">测试数据库连接</a-button>
          <a-button html-type="submit" type="primary" :loading="installing" :disabled="completed">保存配置并开始安装</a-button>
        </footer>
      </a-form>
    </section>
  </main>
</template>

<script setup lang="ts">
import { Message } from '@arco-design/web-vue';
import { reactive, ref } from 'vue';

const form = reactive({
  adminUsername: 'admin',
  adminPassword: '',
  confirmPassword: '',
  databaseHost: 'host.docker.internal',
  databasePort: 3306,
  databaseName: 'quark_vault',
  databaseUser: 'quark_vault',
  databasePassword: '',
  databaseSsl: false,
});
const testing = ref(false);
const installing = ref(false);
const completed = ref(false);

function requestBody() {
  return {
	adminUsername: form.adminUsername,
	adminPassword: form.adminPassword,
    databaseHost: form.databaseHost,
    databasePort: form.databasePort,
    databaseName: form.databaseName,
    databaseUser: form.databaseUser,
    databasePassword: form.databasePassword,
    databaseSsl: form.databaseSsl,
  };
}

async function post(path: string): Promise<{ ok?: boolean; message?: string }> {
  const response = await fetch(`/api/install/${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(requestBody()),
  });
  const result = await response.json().catch(() => ({})) as { ok?: boolean; message?: string };
  if (!response.ok) throw new Error(result.message || '请求失败，请检查服务器日志');
  return result;
}

async function testDatabase() {
  testing.value = true;
  try {
    const result = await post('test');
    Message.success(result.message || '数据库连接成功');
  } catch (error) {
    Message.error((error as Error).message);
  } finally {
    testing.value = false;
  }
}

async function waitForRestart() {
  const startedAt = Date.now();
  while (Date.now() - startedAt < 120_000) {
    await new Promise((resolve) => window.setTimeout(resolve, 1_500));
    try {
      const response = await fetch('/api/install/status', { cache: 'no-store' });
      if (response.ok && !(await response.json() as { required?: boolean }).required) {
        window.location.replace('/admin/login');
        return;
      }
    } catch {
      // A short connection failure is expected while Docker restarts.
    }
  }
  Message.warning('服务重启时间较长，请稍后刷新页面');
}

async function install() {
  if (form.adminPassword !== form.confirmPassword) {
    Message.error('两次输入的后台密码不一致');
    return;
  }
  installing.value = true;
  try {
    await post('complete');
    completed.value = true;
    void waitForRestart();
  } catch (error) {
    Message.error((error as Error).message);
    installing.value = false;
  }
}
</script>

<style scoped>
.install-shell { min-height: 100dvh; display: grid; grid-template-columns: minmax(300px, 420px) minmax(560px, 760px); justify-content: center; align-items: center; gap: clamp(36px, 7vw, 104px); padding: 48px; background: radial-gradient(circle at 20% 15%, rgba(22,93,255,.14), transparent 32%), var(--surface-page); }
.install-intro { display: flex; min-height: 520px; flex-direction: column; justify-content: space-between; color: var(--text-primary); }
.install-brand { display: flex; align-items: center; gap: 12px; font-size: 18px; font-weight: 700; }
.install-brand__mark { display: grid; width: 42px; height: 42px; place-items: center; border-radius: 10px; background: var(--brand-primary); color: white; font-size: 24px; }
.install-kicker { color: var(--brand-primary); font-weight: 600; }
.install-intro h1 { margin: 12px 0 16px; font-size: clamp(36px, 4vw, 54px); line-height: 1.16; letter-spacing: -.04em; }
.install-intro p { max-width: 38em; margin: 0; color: var(--text-secondary); font-size: 16px; }
.install-points { display: grid; gap: 16px; margin: 0; padding: 0; list-style: none; }
.install-points li { display: flex; align-items: center; gap: 12px; color: var(--text-secondary); }
.install-points span { display: grid; width: 28px; height: 28px; flex: 0 0 28px; place-items: center; border-radius: 50%; background: var(--brand-soft); color: var(--brand-primary); font-weight: 700; }
.install-card { padding: clamp(24px, 4vw, 40px); border: 1px solid var(--border-default); border-radius: 8px; background: var(--surface-card); box-shadow: var(--shadow-float); }
.install-card header h2 { margin: 0; font-size: 24px; }
.install-card header p { margin: 6px 0 0; color: var(--text-tertiary); }
.install-tip { margin: 24px 0; }
.install-tip code { overflow-wrap: anywhere; color: var(--brand-primary); }
.install-section-title { margin: 8px 0 14px; padding-bottom: 8px; border-bottom: 1px solid var(--border-default); font-size: 15px; font-weight: 600; }
.ssl-setting { display: flex; min-height: 32px; align-items: center; gap: 10px; color: var(--text-secondary); }
.install-actions { display: flex; justify-content: flex-end; gap: 12px; margin-top: 12px; }
.install-complete { display: flex; align-items: center; gap: 14px; margin: 12px 0; padding: 14px 16px; border-radius: 6px; background: var(--brand-soft); }
.install-complete p { margin: 2px 0 0; color: var(--text-secondary); }
@media (max-width: 980px) { .install-shell { grid-template-columns: minmax(0, 760px); align-items: start; padding: 32px 20px; } .install-intro { min-height: auto; gap: 28px; } .install-points { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 600px) { .install-shell { padding: 0; background: var(--surface-card); } .install-intro { padding: 24px 20px 8px; } .install-intro h1 { font-size: 32px; } .install-points { display: none; } .install-card { padding: 20px; border: 0; border-radius: 0; box-shadow: none; } .install-actions { flex-direction: column-reverse; } .install-actions :deep(.arco-btn) { width: 100%; min-height: 44px; } }
</style>
